// src/main.ts - BUE Web App Core Controller & UI Bootstrap

import { SERVICES, POPULAR_SERVICES, ServiceItem, getServiceById } from './data/services.ts';
import { ICONS, getIcon } from './components/icons.ts';

type TabType = 'home' | 'services' | 'favorites' | 'settings';
type CategoryType = 'all' | 'administrative' | 'academic' | 'student';

class BueWebApp {
  private currentTab: TabType = 'home';
  private selectedCategory: CategoryType = 'all';
  private favorites: Set<string> = new Set();
  private themeMode: 'light' | 'dark' = 'light';
  private activeModalService: ServiceItem | null = null;

  // DOM Elements
  private mainView!: HTMLElement;
  private appHeader!: HTMLElement;
  private headerSearchIcon!: HTMLElement;
  private headerThemeIcon!: HTMLElement;
  private bottomNav!: HTMLElement;
  private serviceModal!: HTMLElement;
  private modalContent!: HTMLElement;
  private searchModal!: HTMLElement;
  private searchInput!: HTMLInputElement;
  private searchResultsList!: HTMLElement;
  private toastContainer!: HTMLElement;

  constructor() {
    this.initStorage();
    this.bindDOMElements();
    this.setupIcons();
    this.setupEventListeners();
    this.applyTheme(this.themeMode, false);
    this.registerPWA();
    this.render();
  }

  private initStorage(): void {
    // Load favorites
    try {
      const savedFavs = localStorage.getItem('bue_favorites_v1');
      if (savedFavs) {
        const parsed = JSON.parse(savedFavs);
        if (Array.isArray(parsed)) {
          this.favorites = new Set(parsed);
        }
      }
    } catch {
      this.favorites = new Set(['hrm', 'it_service_desk', 'student_record_system']);
    }

    // Load theme mode
    try {
      const savedTheme = localStorage.getItem('bue_theme_mode_v1');
      if (savedTheme === 'dark' || savedTheme === 'light') {
        this.themeMode = savedTheme;
      } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        this.themeMode = 'dark';
      }
    } catch {
      this.themeMode = 'light';
    }
  }

  private saveFavorites(): void {
    try {
      localStorage.setItem('bue_favorites_v1', JSON.stringify(Array.from(this.favorites)));
    } catch (e) {
      console.warn('Failed to save favorites to localStorage', e);
    }
  }

  private saveTheme(): void {
    try {
      localStorage.setItem('bue_theme_mode_v1', this.themeMode);
    } catch (e) {
      console.warn('Failed to save theme to localStorage', e);
    }
  }

  private bindDOMElements(): void {
    this.mainView = document.getElementById('main-view') as HTMLElement;
    this.appHeader = document.getElementById('app-header') as HTMLElement;
    this.headerSearchIcon = document.getElementById('header-search-icon') as HTMLElement;
    this.headerThemeIcon = document.getElementById('header-theme-icon') as HTMLElement;
    this.bottomNav = document.getElementById('bottom-nav') as HTMLElement;
    this.serviceModal = document.getElementById('service-modal') as HTMLElement;
    this.modalContent = document.getElementById('modal-content') as HTMLElement;
    this.searchModal = document.getElementById('search-modal') as HTMLElement;
    this.searchInput = document.getElementById('search-input') as HTMLInputElement;
    this.searchResultsList = document.getElementById('search-results-list') as HTMLElement;
    this.toastContainer = document.getElementById('toast-container') as HTMLElement;
  }

  private setupIcons(): void {
    // Header icons
    this.headerSearchIcon.innerHTML = ICONS.search;
    this.headerThemeIcon.innerHTML = this.themeMode === 'dark' ? ICONS.sun : ICONS.moon;

    // Bottom navigation icons
    const iconHome = document.getElementById('icon-tab-home');
    const iconServices = document.getElementById('icon-tab-services');
    const iconFavorites = document.getElementById('icon-tab-favorites');
    const iconSettings = document.getElementById('icon-tab-settings');

    if (iconHome) iconHome.innerHTML = ICONS.home;
    if (iconServices) iconServices.innerHTML = ICONS.services_outline;
    if (iconFavorites) iconFavorites.innerHTML = ICONS.favorites_outline;
    if (iconSettings) iconSettings.innerHTML = ICONS.settings_outline;

    // Modal icons
    const modalCloseIcon = document.getElementById('modal-close-icon');
    if (modalCloseIcon) modalCloseIcon.innerHTML = ICONS.close;

    // Search modal icons
    const searchBackIcon = document.getElementById('search-back-icon');
    if (searchBackIcon) searchBackIcon.innerHTML = ICONS.back;

    const searchInputIcon = document.getElementById('search-input-icon');
    if (searchInputIcon) searchInputIcon.innerHTML = ICONS.search;

    const searchClearIcon = document.getElementById('search-clear-icon');
    if (searchClearIcon) searchClearIcon.innerHTML = ICONS.close;
  }

  private setupEventListeners(): void {
    // Header branding click -> switch to Home
    const branding = document.getElementById('header-branding');
    branding?.addEventListener('click', () => this.switchTab('home'));

    // Header actions
    document.getElementById('btn-header-search')?.addEventListener('click', () => this.openSearchModal());
    document.getElementById('btn-header-theme')?.addEventListener('click', () => this.toggleTheme());

    // Bottom Navigation clicks
    this.bottomNav.querySelectorAll('.nav-item').forEach((item) => {
      item.addEventListener('click', () => {
        const tab = item.getAttribute('data-tab') as TabType;
        if (tab) this.switchTab(tab);
      });
    });

    // Service Modal close
    document.getElementById('btn-close-modal')?.addEventListener('click', () => this.closeServiceModal());
    this.serviceModal.addEventListener('click', (e) => {
      if (e.target === this.serviceModal) this.closeServiceModal();
    });

    // Search Modal events
    document.getElementById('btn-close-search')?.addEventListener('click', () => this.closeSearchModal());
    this.searchInput.addEventListener('input', () => this.handleSearchInput());
    const btnClearSearch = document.getElementById('btn-clear-search');
    btnClearSearch?.addEventListener('click', () => {
      this.searchInput.value = '';
      this.searchInput.focus();
      this.handleSearchInput();
    });

    // Header shadow on scroll
    this.mainView.addEventListener('scroll', () => {
      if (this.mainView.scrollTop > 10) {
        this.appHeader.classList.add('scrolled');
      } else {
        this.appHeader.classList.remove('scrolled');
      }
    });

    // Keyboard ESC to close modals
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.serviceModal.classList.contains('active')) this.closeServiceModal();
        if (this.searchModal.classList.contains('active')) this.closeSearchModal();
      }
    });
  }

  private registerPWA(): void {
    if ('serviceWorker' in navigator && (window.location.protocol === 'https:' || window.location.hostname === 'localhost')) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch((err) => {
          console.warn('PWA ServiceWorker registration error:', err);
        });
      });
    }
  }

  private toggleTheme(): void {
    const nextTheme = this.themeMode === 'light' ? 'dark' : 'light';
    this.applyTheme(nextTheme, true);
  }

  private applyTheme(theme: 'light' | 'dark', showToast: boolean): void {
    this.themeMode = theme;
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${theme}`);
    this.headerThemeIcon.innerHTML = theme === 'dark' ? ICONS.sun : ICONS.moon;
    this.saveTheme();
    if (showToast) {
      this.showToast(`Switched to ${theme === 'dark' ? 'Dark' : 'Light'} theme`);
    }
    // Re-render current view to ensure theme-specific container styles update
    this.render();
  }

  public switchTab(tab: TabType): void {
    if (this.currentTab === tab) return;
    this.currentTab = tab;

    // Update bottom nav UI
    this.bottomNav.querySelectorAll('.nav-item').forEach((item) => {
      const itemTab = item.getAttribute('data-tab');
      if (itemTab === tab) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });

    // Update icons state (filled vs outline)
    const iconHome = document.getElementById('icon-tab-home');
    const iconServices = document.getElementById('icon-tab-services');
    const iconFavorites = document.getElementById('icon-tab-favorites');
    const iconSettings = document.getElementById('icon-tab-settings');

    if (iconHome) iconHome.innerHTML = tab === 'home' ? ICONS.home : ICONS.home_outline;
    if (iconServices) iconServices.innerHTML = tab === 'services' ? ICONS.services : ICONS.services_outline;
    if (iconFavorites) iconFavorites.innerHTML = tab === 'favorites' ? ICONS.favorites : ICONS.favorites_outline;
    if (iconSettings) iconSettings.innerHTML = tab === 'settings' ? ICONS.settings : ICONS.settings_outline;

    this.mainView.scrollTop = 0;
    this.render();
  }

  private toggleFavorite(id: string, e?: Event): void {
    if (e) e.stopPropagation();

    const service = getServiceById(id);
    if (!service) return;

    if (this.favorites.has(id)) {
      this.favorites.delete(id);
      this.showToast(`Removed from favorites`);
    } else {
      this.favorites.add(id);
      this.showToast(`Added ${service.name} to favorites`);
    }

    this.saveFavorites();
    this.render();
  }

  private openService(service: ServiceItem): void {
    if (service.shortcuts && service.shortcuts.length > 0) {
      this.openServiceModal(service);
    } else {
      this.launchUrl(service.url, service.name);
    }
  }

  private launchUrl(url: string, title?: string): void {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
      this.showToast(`Opening ${title || 'Service'}...`);
    } catch (e) {
      console.error('Failed to open URL', e);
      window.location.href = url;
    }
  }

  private openServiceModal(service: ServiceItem): void {
    this.activeModalService = service;
    const isDark = this.themeMode === 'dark';
    const isFav = this.favorites.has(service.id);

    const shortcutsHtml = (service.shortcuts || [])
      .map(
        (sc) => `
        <a class="shortcut-item" href="${sc.targetUrl}" target="_blank" rel="noopener noreferrer">
          <span class="shortcut-title">${sc.title}</span>
          <span class="tile-chevron">${ICONS.external}</span>
        </a>
      `
      )
      .join('');

    this.modalContent.innerHTML = `
      <div class="service-detail-hero">
        <div class="detail-icon-box" style="background-color: ${isDark ? service.iconColor + '2E' : service.bgColor}; color: ${service.iconColor}">
          ${getIcon(service.iconKey)}
        </div>
        <div class="detail-title-info">
          <h2 class="detail-service-name">${service.name}</h2>
          <div class="detail-tags-row">
            <span class="badge-tag">${service.category}</span>
            <span class="badge-tag ${service.isHttps ? 'secure' : 'insecure'}">
              ${service.isHttps ? 'HTTPS' : 'HTTP'}
            </span>
          </div>
        </div>
      </div>

      <p class="detail-description">${service.description}</p>

      ${
        shortcutsHtml
          ? `
        <div class="shortcuts-header">Direct Access Shortcuts</div>
        <div class="shortcuts-list">${shortcutsHtml}</div>
      `
          : ''
      }

      <div style="display: flex; gap: 10px;">
        <button id="btn-modal-fav" class="empty-state-btn" style="flex: 0 0 52px; height: 52px; padding: 0; background-color: var(--surface-chip); color: ${
          isFav ? 'var(--bue-red-accent)' : 'var(--text-muted)'
        }; display: flex; align-items: center; justify-content: center;">
          ${isFav ? ICONS.favorites : ICONS.favorites_outline}
        </button>
        <a class="btn-launch-primary" href="${service.url}" target="_blank" rel="noopener noreferrer" style="flex: 1;">
          <span>Launch ${service.name}</span>
          ${ICONS.external}
        </a>
      </div>
    `;

    document.getElementById('btn-modal-fav')?.addEventListener('click', () => {
      this.toggleFavorite(service.id);
      this.openServiceModal(service);
    });

    this.serviceModal.classList.add('active');
    this.serviceModal.setAttribute('aria-hidden', 'false');
  }

  private closeServiceModal(): void {
    this.serviceModal.classList.remove('active');
    this.serviceModal.setAttribute('aria-hidden', 'true');
    this.activeModalService = null;
  }

  private openSearchModal(): void {
    this.searchModal.classList.add('active');
    this.searchModal.setAttribute('aria-hidden', 'false');
    this.searchInput.value = '';
    this.handleSearchInput();
    setTimeout(() => this.searchInput.focus(), 100);
  }

  private closeSearchModal(): void {
    this.searchModal.classList.remove('active');
    this.searchModal.setAttribute('aria-hidden', 'true');
  }

  private handleSearchInput(): void {
    const query = this.searchInput.value.trim().toLowerCase();
    const btnClear = document.getElementById('btn-clear-search');
    if (btnClear) btnClear.style.display = query ? 'flex' : 'none';

    const matches = SERVICES.filter((s) => {
      if (!query) return true;
      return (
        s.name.toLowerCase().includes(query) ||
        s.description.toLowerCase().includes(query) ||
        s.category.toLowerCase().includes(query) ||
        s.keywords.some((k) => k.toLowerCase().includes(query))
      );
    });

    if (matches.length === 0) {
      this.searchResultsList.innerHTML = `
        <div class="empty-state-view">
          <div class="empty-state-icon-box">${ICONS.search}</div>
          <div class="empty-state-title">No matching services</div>
          <div class="empty-state-subtitle">No university portals match "${this.escapeHtml(query)}". Try another search keyword.</div>
        </div>
      `;
      return;
    }

    this.searchResultsList.innerHTML = matches
      .map((service) => this.renderServiceListTile(service))
      .join('');

    // Attach click listeners
    this.attachTileListeners(this.searchResultsList);
  }

  private showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerText = message;
    this.toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(-10px)';
      toast.style.transition = 'all 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 2400);
  }

  private escapeHtml(str: string): string {
    return str.replace(/[&<>'"]/g, (tag) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag));
  }

  // View Renderers
  private render(): void {
    switch (this.currentTab) {
      case 'home':
        this.renderHome();
        break;
      case 'services':
        this.renderServices();
        break;
      case 'favorites':
        this.renderFavorites();
        break;
      case 'settings':
        this.renderSettings();
        break;
    }
  }

  private renderHome(): void {
    const isDark = this.themeMode === 'dark';

    // First 9 Popular Services in exact priority order
    const popularCardsHtml = POPULAR_SERVICES.map((s) => {
      const isFav = this.favorites.has(s.id);
      return `
        <div class="service-card" data-service-id="${s.id}">
          <button class="service-card-fav-btn ${isFav ? 'active' : ''}" data-fav-id="${s.id}" aria-label="Toggle favorite">
            ${isFav ? ICONS.favorites : ICONS.favorites_outline}
          </button>
          <div class="service-icon-box" style="background-color: ${isDark ? s.iconColor + '2E' : s.bgColor}; color: ${s.iconColor};">
            ${getIcon(s.iconKey)}
          </div>
          <span class="service-card-title">${s.name}</span>
        </div>
      `;
    }).join('');

    this.mainView.innerHTML = `
      <section class="welcome-section">
        <h1 class="welcome-title">Welcome to BUE</h1>
        <p class="welcome-subtitle">Quick access to university portals & services</p>
      </section>

      <!-- Search Trigger Entry Bar -->
      <section class="search-trigger-container">
        <div id="search-bar-trigger" class="search-trigger-bar" role="button" tabindex="0">
          <span class="search-icon-box">${ICONS.search}</span>
          <span class="search-placeholder">Search all services...</span>
          <span class="search-action-pill">20 Services</span>
        </div>
      </section>

      <!-- Campus Hero Banner -->
      <section class="hero-banner-container">
        <div id="hero-banner-card" class="hero-banner-card">
          <img
            src="/assets/images/bue_campus_banner.png"
            alt="BUE Campus"
            class="hero-banner-img"
            onerror="this.onerror=null; this.src='/assets/images/bue_campus_hero.jpg';"
          />
        </div>
      </section>

      <!-- Section Header -->
      <div class="section-header">
        <h2 class="section-title">Popular Services</h2>
        <button id="btn-see-all" class="section-link-btn">See All</button>
      </div>

      <!-- 3-Column Responsive Grid with the 9 Priority Services in Exact Order -->
      <div class="services-grid-3">
        ${popularCardsHtml}
      </div>
    `;

    // Bind Home events
    document.getElementById('search-bar-trigger')?.addEventListener('click', () => this.openSearchModal());
    document.getElementById('btn-see-all')?.addEventListener('click', () => this.switchTab('services'));
    document.getElementById('hero-banner-card')?.addEventListener('click', () => this.switchTab('services'));

    this.mainView.querySelectorAll('.service-card').forEach((card) => {
      const id = card.getAttribute('data-service-id');
      if (!id) return;
      const service = getServiceById(id);
      if (!service) return;

      card.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.service-card-fav-btn')) return;
        this.openService(service);
      });
    });

    this.mainView.querySelectorAll('.service-card-fav-btn').forEach((btn) => {
      const id = btn.getAttribute('data-fav-id');
      if (id) {
        btn.addEventListener('click', (e) => this.toggleFavorite(id, e));
      }
    });
  }

  private renderServices(): void {
    const categories: { id: CategoryType; label: string; count: number }[] = [
      { id: 'all', label: 'All', count: SERVICES.length },
      { id: 'administrative', label: 'Administrative', count: SERVICES.filter((s) => s.category === 'administrative').length },
      { id: 'academic', label: 'Academic', count: SERVICES.filter((s) => s.category === 'academic').length },
      { id: 'student', label: 'Student', count: SERVICES.filter((s) => s.category === 'student').length },
    ];

    const categoryPillsHtml = categories
      .map(
        (cat) => `
        <button class="category-pill ${this.selectedCategory === cat.id ? 'active' : ''}" data-category="${cat.id}">
          ${cat.label} (${cat.count})
        </button>
      `
      )
      .join('');

    const filtered = SERVICES.filter((s) => {
      if (this.selectedCategory === 'all') return true;
      return s.category === this.selectedCategory;
    });

    const tilesHtml = filtered.map((service) => this.renderServiceListTile(service)).join('');

    this.mainView.innerHTML = `
      <section class="welcome-section" style="padding-bottom: 8px;">
        <h1 class="welcome-title">All Services</h1>
        <p class="welcome-subtitle">${SERVICES.length} University Services Available</p>
      </section>

      <!-- Category Filter Pills -->
      <div class="category-filter-bar">
        ${categoryPillsHtml}
      </div>

      <!-- Service Items -->
      <div id="services-list-container">
        ${tilesHtml}
      </div>
    `;

    // Category pill listeners
    this.mainView.querySelectorAll('.category-pill').forEach((pill) => {
      pill.addEventListener('click', () => {
        const cat = pill.getAttribute('data-category') as CategoryType;
        if (cat) {
          this.selectedCategory = cat;
          this.renderServices();
        }
      });
    });

    this.attachTileListeners(this.mainView);
  }

  private renderFavorites(): void {
    const favServices = SERVICES.filter((s) => this.favorites.has(s.id));

    if (favServices.length === 0) {
      this.mainView.innerHTML = `
        <section class="welcome-section" style="padding-bottom: 8px;">
          <h1 class="welcome-title">Favorites</h1>
          <p class="welcome-subtitle">Your personal quick access catalogue</p>
        </section>
        <div class="empty-state-view">
          <div class="empty-state-icon-box">${ICONS.favorites_outline}</div>
          <div class="empty-state-title">No Favorites Yet</div>
          <div class="empty-state-subtitle">Tap the heart icon on any university service to add it to your personal quick access list.</div>
          <button id="btn-browse-services" class="empty-state-btn">Browse All Services</button>
        </div>
      `;

      document.getElementById('btn-browse-services')?.addEventListener('click', () => this.switchTab('services'));
      return;
    }

    const tilesHtml = favServices.map((service) => this.renderServiceListTile(service)).join('');

    this.mainView.innerHTML = `
      <section class="welcome-section" style="padding-bottom: 8px;">
        <h1 class="welcome-title">Favorites</h1>
        <p class="welcome-subtitle">${favServices.length} Saved Services for Quick Access</p>
      </section>
      <div id="favorites-list-container">
        ${tilesHtml}
      </div>
    `;

    this.attachTileListeners(this.mainView);
  }

  private renderSettings(): void {
    this.mainView.innerHTML = `
      <section class="welcome-section" style="padding-bottom: 12px;">
        <h1 class="welcome-title">Settings</h1>
        <p class="welcome-subtitle">App preferences and university resources</p>
      </section>

      <!-- Appearance -->
      <div class="settings-group">
        <div class="settings-group-header">Appearance</div>
        <div id="setting-toggle-theme" class="settings-row">
          <div class="settings-label-box">
            <div class="settings-icon">${this.themeMode === 'dark' ? ICONS.sun : ICONS.moon}</div>
            <div>
              <div class="settings-text">Theme Mode</div>
              <div class="settings-subtext">Current: ${this.themeMode === 'dark' ? 'Dark Mode' : 'Light Mode'}</div>
            </div>
          </div>
          <span class="search-action-pill">Toggle</span>
        </div>
      </div>

      <!-- University Quick Portals -->
      <div class="settings-group">
        <div class="settings-group-header">Official Portals</div>
        <a class="settings-row" href="https://www.bue.edu.eg" target="_blank" rel="noopener noreferrer" style="text-decoration: none;">
          <div class="settings-label-box">
            <div class="settings-icon">${ICONS.school}</div>
            <div>
              <div class="settings-text">BUE Official Website</div>
              <div class="settings-subtext">bue.edu.eg</div>
            </div>
          </div>
          <span class="tile-chevron">${ICONS.external}</span>
        </a>
        <a class="settings-row" href="https://servicedesk.bue.edu.eg/home" target="_blank" rel="noopener noreferrer" style="text-decoration: none;">
          <div class="settings-label-box">
            <div class="settings-icon">${ICONS.headset}</div>
            <div>
              <div class="settings-text">IT Service Desk Support</div>
              <div class="settings-subtext">Technical assistance portal</div>
            </div>
          </div>
          <span class="tile-chevron">${ICONS.external}</span>
        </a>
      </div>

      <!-- App Governance & Data -->
      <div class="settings-group">
        <div class="settings-group-header">Application & Data</div>
        <div id="setting-clear-favorites" class="settings-row">
          <div class="settings-label-box">
            <div class="settings-icon" style="color: var(--bue-red-accent);">${ICONS.favorites_outline}</div>
            <div>
              <div class="settings-text">Reset Saved Favorites</div>
              <div class="settings-subtext">Remove all custom saved shortcuts</div>
            </div>
          </div>
          <span class="search-action-pill" style="color: var(--bue-red-accent); background-color: var(--bue-red-soft);">Reset</span>
        </div>
        <div class="settings-row" style="cursor: default;">
          <div class="settings-label-box">
            <div class="settings-icon">${ICONS.badge}</div>
            <div>
              <div class="settings-text">BUE University Services Launcher</div>
              <div class="settings-subtext">Version 1.0.0 (Web Production) • English (en)</div>
            </div>
          </div>
        </div>
      </div>
    `;

    document.getElementById('setting-toggle-theme')?.addEventListener('click', () => this.toggleTheme());
    document.getElementById('setting-clear-favorites')?.addEventListener('click', () => {
      this.favorites.clear();
      this.saveFavorites();
      this.showToast('All favorites cleared');
      this.render();
    });
  }

  private renderServiceListTile(service: ServiceItem): string {
    const isDark = this.themeMode === 'dark';
    const isFav = this.favorites.has(service.id);

    return `
      <div class="service-list-tile" data-service-id="${service.id}">
        <div class="tile-icon-box" style="background-color: ${isDark ? service.iconColor + '2E' : service.bgColor}; color: ${service.iconColor}">
          ${getIcon(service.iconKey)}
        </div>
        <div class="tile-content">
          <div class="tile-title-row">
            <h3 class="tile-title">${service.name}</h3>
            <span class="tile-category-tag">${service.category}</span>
          </div>
          <p class="tile-desc">${service.description}</p>
        </div>
        <div class="tile-actions">
          <button class="tile-fav-btn ${isFav ? 'active' : ''}" data-fav-id="${service.id}" aria-label="Toggle favorite">
            ${isFav ? ICONS.favorites : ICONS.favorites_outline}
          </button>
          <span class="tile-chevron">${ICONS.chevron_right}</span>
        </div>
      </div>
    `;
  }

  private attachTileListeners(container: HTMLElement): void {
    container.querySelectorAll('.service-list-tile').forEach((tile) => {
      const id = tile.getAttribute('data-service-id');
      if (!id) return;
      const service = getServiceById(id);
      if (!service) return;

      tile.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.tile-fav-btn')) return;
        this.openService(service);
      });
    });

    container.querySelectorAll('.tile-fav-btn').forEach((btn) => {
      const id = btn.getAttribute('data-fav-id');
      if (id) {
        btn.addEventListener('click', (e) => this.toggleFavorite(id, e));
      }
    });
  }
}

// Bootstrap application on DOMContentLoaded
window.addEventListener('DOMContentLoaded', () => {
  new BueWebApp();
});
